import { endpointDeclaration } from '@moojo/endpoints'
import { z } from 'zod'
/**
 * Quick Login
 *
 * This endpoint handles the authentication flow for applications that need to associate their users
 * with Moojo accounts using phone number verification. The flow consists of three main steps:
 *
 * 1. JOIN: Initial user registration
 *    - Associates a host application's user ID with a phone number
 *    - Triggers sending of a verification code to the provided phone number
 *    - Returns a 'codeSent' response indicating the code was dispatched
 *
 * 2. RECONNECT: Returning user verification
 *    - Used when a previously registered user returns to the application
 *    - Checks if the hostUserId has been previously registered
 *    - If found, sends a new verification code to the associated phone number and returns 'codeSent'
 *    - If not found, returns 'joinNeeded' to indicate the user needs to complete the join step first
 *
 * 3. LOGIN: Authentication completion
 *    - Verifies the code provided by the user against the expected verification code
 *    - If successful, returns a success response with authentication token and user details
 *    - If the hostUserId hasn't been registered, returns 'joinNeeded'
 *    - If the verification code is incorrect, returns a 401 Unauthorized error
 *
 * Recommended flow:
 *   1. Start with 'reconnect' for all users
 *   2.
 *      - If response is 'joinNeeded': Call 'join' to register the user (sends verification code)
 *      - If response is 'codeSent': A verification code was sent to the user's phone
 *   3. Call 'login' with the user's verification code
 *   4. On 'success' response: Use the returned authentication token for subsequent API calls
 *
 * This approach eliminates the need for applications to track user registration status.
 */
export const QuickLoginRequest = z.discriminatedUnion('step', [
  z.object({
    /** The initial step where a user provides their phone number */
    step: z.literal('join'),
    /** Unique identifier for the user in the calling application */
    hostUserId: z.string(),
    /** User's phone number for verification */
    phoneNumber: z.string(),
  }),
  z.object({
    /** Step for returning users who previously registered their phone number */
    step: z.literal('reconnect'),
    /** Unique identifier for the user in the calling application */
    hostUserId: z.string(),
  }),
  z.object({
    /** Final verification step to complete the login process */
    step: z.literal('login'),
    /** Unique identifier for the user in the calling application */
    hostUserId: z.string(),
    /** Verification code sent to the user via SMS */
    verificationCode: z.string(),
  }),
])

export type QuickLoginRequest = z.infer<typeof QuickLoginRequest>

export const QuickLoginResponse = z.discriminatedUnion('tag', [
  z.object({
    /** Indicates the user needs to complete the join step first */
    tag: z.literal('joinNeeded'),
  }),
  z.object({
    /** Indicates a verification code has been sent to the user */
    tag: z.literal('codeSent'),
  }),
  z.object({
    /** Indicates a successful login */
    tag: z.literal('success'),
    /**  Authentication token for the logged-in user (to be used a Bearer token in subsequent calls) */
    token: z.string(),
    /** ISO8601 timestamp when the token expires */
    expiresAtIso8601: z.string(),
    /** UUID of the logged-in user */
    publicUserId: z.string().uuid(),
    appId: z.string(),
  }),
])

export type QuickLoginResponse = z.infer<typeof QuickLoginResponse>

export const quickLoginDeclaration = endpointDeclaration(QuickLoginRequest, QuickLoginResponse)
